/**
 * Slugs of the models that serve a mode of their own instead of ordinary chat
 * turns. Kept apart from the store so admin code can read them without pulling
 * in pinia and the model queries.
 */

/** Azure image-generation deployment; like web search it is never listed by the API. */
export const IMAGE_GENERATION_MODEL = 'gpt-image-1'

/**
 * The API never lists the web-search model, so it is seeded into the lookup maps
 * by hand. It has been returned on messages both bare and foundry-prefixed, and
 * both spellings still exist in stored chats.
 */
export const PERPLEXITY_SLUGS = ['perplexity/sonar', '@px/perplexity/sonar']

/** Deployment name of every image-generation model (`gpt-image-1`, `gpt-image-1-mini`). */
const IMAGE_GENERATION_DEPLOYMENT_PREFIX = 'gpt-image-'

/** Segment pair every web-search slug carries (`sonar`, `sonar-pro`, `sonar-reasoning`). */
const WEB_SEARCH_SLUG_FRAGMENT = 'perplexity/sonar'

/** Last `/`-segment of a slug (the deployment name), lowercased. */
function modelDeploymentName(slug: string): string {
  return slug.trim().toLowerCase().split('/').pop() ?? ''
}

/**
 * Whether a slug is exactly the `gpt-image-1` deployment (bare or
 * foundry/Azure-prefixed). Does not match variants such as `gpt-image-1-mini`.
 */
export function isGptImage1ModelSlug(slug: string): boolean {
  return modelDeploymentName(slug) === IMAGE_GENERATION_MODEL
}

/** Whether a slug names the web-search model, in any of its deployment variants. */
export function isWebSearchModelSlug(slug: string): boolean {
  return slug.trim().toLowerCase().includes(WEB_SEARCH_SLUG_FRAGMENT)
}

/**
 * Whether a slug names a mode rather than a chat model: image generation, driven
 * by the backend's `image_generation_model` setting, or web search, driven by
 * `web_search_model`. Neither is listed by `GET /llms` and neither answers a
 * chat completion, so neither belongs anywhere a chat model is chosen.
 *
 * Matched on shape, not on an exact list: the `@foundry/region/` routing prefix
 * differs per environment and both modes get deployment variants, so a literal
 * comparison would quietly stop matching the day one is renamed.
 */
export function isSpecialPurposeModelSlug(slug: string): boolean {
  const normalized = slug.trim().toLowerCase()
  if (!normalized) return false
  const deployment = modelDeploymentName(normalized)
  return (
    deployment.startsWith(IMAGE_GENERATION_DEPLOYMENT_PREFIX) ||
    isWebSearchModelSlug(normalized)
  )
}
